import { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { Button, Card, CardContent } from '@/ui/primitives';
import { NotifyDone } from '@/components/NotifyDone';
import { NotifyForm } from '@/components/NotifyForm';
import type { BandId, NotifyIntent } from '@/domain/bands';
import type { Dictionary } from '@/i18n';
import type { LeadResult } from '@/services/leads';

type NotifyDialogProps = {
  dict: Dictionary;
  intent: NotifyIntent;
  band: BandId;
  inputType: 'cep' | 'geolocation';
  onSubmit: (firstName: string, phone: string) => Promise<LeadResult>;
};

export const NotifyDialog = ({ dict, intent, band, inputType, onSubmit }: NotifyDialogProps) => {
  const dialog = useRef<HTMLDialogElement | null>(null);
  const [open, setOpen] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const copy = dict.notify[intent];

  /* `<dialog>` carries focus trapping, Escape and the backdrop, which a hand-rolled modal
     gets wrong often enough to be worth the fallback: where showModal is missing the
     dialog opens inline instead, so the lead can still ask. */
  useEffect(() => {
    const element = dialog.current;
    if (!element) {
      return;
    }
    if (!open) {
      if (element.open) {
        element.close();
      }
      return;
    }
    /* Try it and let it fail, the way golden rule 5 treats WebGL: a browser predating
       <dialog> throws here, and that is the signal to open it inline instead. */
    try {
      element.showModal();
    } catch {
      element.open = true;
    }
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  /* Escape and the backdrop close the element without going through our button, so the
     state has to follow the element rather than the other way round. */
  useEffect(() => {
    const element = dialog.current;
    if (!element) {
      return;
    }
    element.addEventListener('close', close);
    return () => {
      element.removeEventListener('close', close);
    };
  }, [close]);

  if (recorded && !open) {
    return <NotifyDone dict={dict} intent={intent} />;
  }

  return (
    <>
      {recorded ? null : (
        /* Solid, not ghost. A transparent button inside a white card does not read as
           one at all, and on these bands it is the only thing left for the lead to do:
           the answer they just got has no station worth going to. */
        <Button
          type="button"
          variant="accent"
          className="h-12 w-full text-[15px]"
          onClick={() => {
            setOpen(true);
          }}
        >
          <BellRing className="h-4 w-4" aria-hidden="true" />
          {copy.trigger}
        </Button>
      )}

      <dialog
        ref={dialog}
        aria-labelledby="notify-title"
        /* `m-auto` restores what the UA stylesheet centres a modal with; the Tailwind
           reset zeroes it, which parks the dialog in the top-left corner. */
        className="m-auto w-[min(28rem,calc(100vw-2rem))] rounded-xl border border-grey-200 bg-transparent p-0 backdrop:bg-black/40"
      >
        {/* Rendered only while open. A closed <dialog> keeps its children in the DOM,
            where they stay reachable by anything that does not read the UA stylesheet,
            and remounting also clears whatever the lead typed and abandoned. */}
        {open ? (
          <Card className="border-none">
            <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2
                  id="notify-title"
                  className="text-base font-extrabold tracking-tight text-black"
                >
                  {recorded ? dict.notify.doneTitle : copy.title}
                </h2>
                <p className="text-sm leading-relaxed text-grey-800">
                  {recorded ? copy.done : copy.body}
                </p>
              </div>
              <button
                type="button"
                aria-label={dict.notify.close}
                onClick={close}
                className="-mr-1 -mt-1 rounded p-1 text-grey-800"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            {recorded ? null : (
              <NotifyForm
                dict={dict}
                intent={intent}
                band={band}
                inputType={inputType}
                onSubmit={onSubmit}
                onRecorded={() => {
                  setRecorded(true);
                }}
              />
            )}
            </CardContent>
          </Card>
        ) : null}
      </dialog>
    </>
  );
};
